import React, {
  type ComponentProps,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import {
  Pressable,
  type PressableStateCallbackType,
  type StyleProp,
  StyleSheet,
  View,
  type ViewInstance,
  type ViewStyle,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import useMergedRef from '@react-hook/merged-ref';

import { webOnlyStyles } from '../../../../../utils/webOnlyStyles';
import { type ButtonFluidPressablePalette, paletteStyles } from './ButtonFluidPressable.pallets';

// ─────────────────────────────────────────────────────────────────────────────

export const ButtonFluidPressable = forwardRef<ViewInstance, ButtonFluidPressableProps>(
  (props, ref) => {
    const {
      style,
      disabled = false,
      palette = 'kale',
      safeAreaEdge,
      isLoading = false,
      onPress,
      ...restProps
    } = props;

    // ─── Refs ────────────────────────────────────────────────────────────────

    const localRef = useRef(null);
    const mergedRef = useMergedRef(localRef, ref);

    // ─── Safe Area ───────────────────────────────────────────────────────────

    const safeAreaInsets = useSafeAreaInsets();

    const shouldUseSafeAreaEdgeTop = safeAreaEdge === 'top' || safeAreaEdge === 'vertical';
    const shouldUseSafeAreaEdgeBottom = safeAreaEdge === 'bottom' || safeAreaEdge === 'vertical';

    // ─── Styles ──────────────────────────────────────────────────────────────

    const safeAreaStyles = useStyle(() => {
      const { top, bottom } = safeAreaInsets;

      return {
        paddingTop: shouldUseSafeAreaEdgeTop ? Math.max(PRESSABLE_PADDING, top) : PRESSABLE_PADDING,
        paddingBottom: shouldUseSafeAreaEdgeBottom
          ? Math.max(PRESSABLE_PADDING, bottom)
          : PRESSABLE_PADDING,
      };
    }, [safeAreaInsets.top, safeAreaInsets.bottom]);

    const pressableStyles = useCallback(
      (state: PressableStateCallbackType): StyleProp<ViewStyle> => {
        const { pressed, hovered } = state as InteractionState;

        return [
          styles.pressable,
          pressableWebStyles,
          safeAreaStyles,
          disabled ? paletteStyles.disabled.idle : paletteStyles[palette].idle,
          hovered && !disabled ? paletteStyles[palette].hover : undefined,
          pressed && !disabled ? styles.pressablePressed : undefined,
          style,
        ];
      },
      [disabled, palette, safeAreaStyles, style],
    );

    // ─────────────────────────────────────────────────────────────────────────

    return (
      <View>
        {isLoading ? <ButtonFluidPressableLoadingBar /> : null}

        <Pressable
          ref={mergedRef}
          accessibilityRole="button"
          disabled={disabled}
          style={pressableStyles}
          accessibilityState={{ busy: isLoading, disabled: Boolean(disabled) }}
          {...restProps}
          onPress={onPress}
        />
      </View>
    );
  },
);

// ─── Components ──────────────────────────────────────────────────────────────

const ButtonFluidPressableLoadingBar = () => {
  const opacity = useSharedValue(1);

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    opacity.value = withRepeat(
      withSequence(
        withTiming(0, { duration: 0 }),
        withTiming(0.5, { duration: 1000 }),
        withTiming(0, { duration: 1000 }),
      ),
      -1,
    );
  }, [opacity]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const animatedStyle = useAnimatedStyle(() => {
    return { opacity: opacity.value };
  });

  // ─────────────────────────────────────────────────────────────────────

  return <Animated.View style={[styles.pressableLoadingBar, animatedStyle]} pointerEvents="none" />;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PRESSABLE_PADDING = theme.spacing['6'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    width: '100%',
    minHeight: 72,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
    backgroundColor: theme.colors.KALE, // default background color
    padding: theme.spacing['6'],
    userSelect: 'none',
  },
  pressablePressed: {
    opacity: 0.8,
  },
  pressableLoadingBar: {
    height: 4,
    backgroundColor: theme.colors.KALE,
    position: 'absolute',
    top: -4,
    left: 0,
    width: '100%',
    opacity: 0,
  },
});

const pressableWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
  transition: theme.transitions.presets.colorsAndOpacity,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ButtonFluidPressableProps = {
  safeAreaEdge?: 'top' | 'bottom' | 'vertical';
  style?: StyleProp<ViewStyle>;

  /**
   * @default "kale"
   */
  palette?: ButtonFluidPressablePalette;
  isLoading?: boolean;
} & Omit<ComponentProps<typeof Pressable>, 'style'>;

type InteractionState = {
  hovered: boolean;
} & PressableStateCallbackType;
